import { forwardRef, useState } from 'react';
import clsx from 'clsx';
import './ElementCard.css';

interface ElementCardProps {
  className?: string;
  classNameImg?: string;
  onClick?: () => void;
  text: string;
  imgSrc?: string;
  imageHover?: string;
  state?: 'default' | 'active';
  type: 'default' | 'canvas';
  size?: 'default' | 'small' | 'extra-small';
  shadow?: string;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
  'data-qa-selector'?: string;
}

export const ElementCard = forwardRef<HTMLDivElement, ElementCardProps>(
  (
    {
      className = '',
      classNameImg = '',
      onClick,
      imgSrc,
      imageHover,
      text = 'Text',
      state = 'default',
      type = 'default',
      size = 'default',
      shadow,
      onDragStart,
      onDragEnd,
      'data-qa-selector': dataQaSelector,
    },
    ref,
  ) => {
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = () => {
      if (onClick) onClick();
    };

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    return (
      <div
        ref={ref}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-qa-selector={dataQaSelector}
        className={clsx(
          'group relative inline-flex cursor-pointer flex-col items-center justify-center gap-2.5 rounded-lg outline outline-offset-[-1px] transition-all hover:outline-2 hover:outline-indigo-500',
          `element-card element-card__size-${size}`,
          {
            'outline-2 outline-indigo-500': state === 'active',
            'text-[#575f6a] outline-[#e9ecf0]': state === 'default',
            'bg-neutral-50 text-neutral-500 outline-neutral-100': type === 'default',
            'bg-white text-[#575f6a]': type === 'canvas',
          },
          className,
        )}
      >
        {imgSrc && (
          <div
            className={clsx('relative h-7 w-8 rounded-lg transition-all duration-300 ease-in-out', {
              'w-10': isHovered || state === 'active',
              'w-8': !isHovered && state === 'default',
            })}
          >
            <div
              className="element-card__icon-bg absolute inset-0 z-0 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.9)' }}
              aria-hidden="true"
            />
            <div
              style={{
                backgroundImage: `url("${isHovered && imageHover ? imageHover : imgSrc}")`,
              }}
              className={clsx(
                'relative z-10 h-7 w-full rounded-lg bg-cover bg-center transition-all duration-300 ease-in-out group-hover:opacity-75',
                isHovered && shadow ? shadow : null,
                classNameImg,
              )}
            />
          </div>
        )}
        {text && (
          <p className="element-card__text text-subtitle-xs relative z-10 font-medium">{text}</p>
        )}
      </div>
    );
  },
);

ElementCard.displayName = 'ElementCard';
